import React, { useEffect, useMemo, useState } from "react";
import { Col, Row, Spinner } from "reactstrap";
import { H3, H5 } from "../../../../AbstractElements";
import axios from "axios";

interface LegalDocRequest {
  document_request_id: number;
  request_created: string | null; // "YYYY-MM-DD HH:mm:ss" (you set dateStrings)
  status: string | null;
  opportunity: string | null;
  opp_name_lookup: string | null;
  client_name: string | null;
  document_type: string | null;

  baa_needed: 0 | 1 | null;
  account_manager: string | null;

  comments: string | null;
  reason_no_draft_required: string | null;
  recruit: string | null;

  draft_created: 0 | 1 | null;
  finance_reviewed: 0 | 1 | null;
  am_reviewed: 0 | 1 | null;
  signed_by_client: 0 | 1 | null;

  link_to_draft_sow: string | null;
  signing_partner: string | null;
  link_to_existing_msa: string | null;
  link_to_existing_nda: string | null;

  draft_completed_date: string | null;
  reason_no_finance_review_req: string | null;

  created_at?: string;
  updated_at?: string;
}

type SortKey = keyof LegalDocRequest;

interface LegalDocRequestsReportProps {
  reportTitle?: string;
  filterStatus?: string; // optional filter (client side)
}

const LegalDocRequestsReport: React.FC<LegalDocRequestsReportProps> = ({
  reportTitle = "Legal Doc Requests",
  filterStatus,
}) => {
  const [data, setData] = useState<LegalDocRequest[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sortConfig, setSortConfig] = useState<{
    key: SortKey;
    direction: "asc" | "desc";
  } | null>(null);

  const parseDate = (s: string | null) => {
    if (!s) return null;
    // Handles "YYYY-MM-DD HH:mm:ss"
    const d = new Date(s.replace(" ", "T") + "Z"); // treat as UTC-ish; adjust if needed
    return isNaN(d.getTime()) ? null : d;
  };

  const daysBetween = (start: Date | null, end: Date | null) => {
    if (!start || !end) return null;
    const ms = end.getTime() - start.getTime();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // You can pass server-side filters via query string later (status, limit, etc)
      const response = await axios.get<LegalDocRequest[]>(
        `/legal-doc-requests?limit=500&orderBy=request_created&orderDir=DESC`
      );

      // Optional client-side status filter
      let rows = response.data ?? [];
      if (filterStatus) {
        rows = rows.filter(
          (r) => (r.status ?? "").toLowerCase() === filterStatus.toLowerCase()
        );
      }

      setData(rows);
    } catch (err: any) {
      console.error("Error fetching legal doc requests:", err);
      setError(err?.message ?? "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const sortedData = useMemo(() => {
    if (!data) return [];
    if (!sortConfig) return data;

    const { key, direction } = sortConfig;

    const getValue = (row: LegalDocRequest) => {
      const v = row[key];

      // Dates: sort by timestamp
      if (key === "request_created" || key === "draft_completed_date") {
        return parseDate(v as string | null)?.getTime() ?? 0;
      }

      // Numbers: sort numerically
      if (key === "document_request_id") return row.document_request_id;

      // Booleans: treat null as -1 so nulls float last
      if (
        key === "draft_created" ||
        key === "finance_reviewed" ||
        key === "am_reviewed" ||
        key === "signed_by_client" ||
        key === "baa_needed"
      ) {
        return v === null ? -1 : v;
      }

      return (v ?? "").toString().toLowerCase();
    };

    return [...data].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);

      if (av === undefined && bv === undefined) return 0;
      if (av === undefined) return 1;
      if (bv === undefined) return -1;

      if (av < bv) return direction === "asc" ? -1 : 1;
      if (av > bv) return direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  const sortData = (key: SortKey) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const summary = useMemo(() => {
    const rows = sortedData;
    const now = new Date();

    const total = rows.length;

    const byStatus: Record<string, number> = {};
    rows.forEach((r) => {
      const s = (r.status ?? "Unknown").trim() || "Unknown";
      byStatus[s] = (byStatus[s] ?? 0) + 1;
    });

    const openRows = rows.filter((r) => !r.draft_completed_date);
    const avgOpenDays = (() => {
      const values = openRows
        .map((r) => daysBetween(parseDate(r.request_created), now))
        .filter((v): v is number => v !== null);
      if (values.length === 0) return null;
      return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
    })();

    const completedRows = rows.filter((r) => !!r.draft_completed_date);
    const avgCycleDays = (() => {
      const values = completedRows
        .map((r) =>
          daysBetween(
            parseDate(r.request_created),
            parseDate(r.draft_completed_date)
          )
        )
        .filter((v): v is number => v !== null);
      if (values.length === 0) return null;
      return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
    })();

    return { total, byStatus, avgOpenDays, avgCycleDays };
  }, [sortedData]);

  if (loading) {
    return (
      <>
        <div>Pulling Legal Doc Requests from the database…</div>
        <Spinner color='primary' />
      </>
    );
  }

  if (error) return <div>Error: {error}</div>;

  return (
    <Col xs='12'>
      <style>{`
        .ldr-table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
          font-size: 1rem;
          text-align: left;
        }
        .ldr-table th, .ldr-table td {
          padding: 12px 15px;
          border: 1px solid #ddd;
          vertical-align: top;
        }
        .ldr-table th {
          background-color: #f4f4f4;
          font-weight: bold;
          cursor: pointer;
        }
        .even-row { background-color: #f9f9f9; }
        .odd-row { background-color: #fff; }
        .ldr-table tr:hover { background-color: #f1f1f1; }

        .muted { color: #666; }
        .chip {
          display: inline-block;
          padding: 2px 10px;
          border-radius: 999px;
          background: #eee;
          font-size: 0.85rem;
        }
        .chip-ok { background: #e6f6ea; }
        .chip-no { background: #fdeaea; }
        .link { word-break: break-all; }
      `}</style>

      <Row>
        <Col xs='12'>
          <H3 className='font-primary'>{reportTitle}</H3>
        </Col>
      </Row>

      <Row></Row>

      <Row>
        <Col xs='12'>
          <table className='ldr-table'>
            <thead>
              <tr>
                <th onClick={() => sortData("document_request_id")}>
                  Request ID
                </th>
                <th onClick={() => sortData("request_created")}>Created</th>
                <th onClick={() => sortData("status")}>Status</th>
                <th onClick={() => sortData("client_name")}>Client</th>
                <th onClick={() => sortData("document_type")}>Doc Type</th>
                <th onClick={() => sortData("account_manager")}>
                  Account Manager
                </th>
                <th onClick={() => sortData("draft_created")}>Draft?</th>
                <th onClick={() => sortData("finance_reviewed")}>Finance?</th>
                <th onClick={() => sortData("am_reviewed")}>AM?</th>
                <th onClick={() => sortData("signed_by_client")}>Signed?</th>
                <th onClick={() => sortData("draft_completed_date")}>
                  Completed
                </th>
                <th>Links</th>
                <th>Notes</th>
              </tr>
            </thead>

            <tbody>
              {sortedData.map((r, index) => {
                const created = parseDate(r.request_created);
                const completed = parseDate(r.draft_completed_date);
                const ageDays = daysBetween(created, completed ?? new Date());

                const chip = (v: 0 | 1 | null) => {
                  if (v === 1) return <span className='chip chip-ok'>Yes</span>;
                  if (v === 0) return <span className='chip chip-no'>No</span>;
                  return <span className='chip'>—</span>;
                };

                return (
                  <tr
                    key={r.document_request_id}
                    className={index % 2 === 0 ? "even-row" : "odd-row"}
                  >
                    <td>{r.document_request_id}</td>
                    <td>
                      <div>{r.request_created ?? "—"}</div>
                      {ageDays !== null ? (
                        <div className='muted'>
                          {completed
                            ? `Cycle: ${ageDays}d`
                            : `Open: ${ageDays}d`}
                        </div>
                      ) : null}
                    </td>
                    <td>{r.status ?? "—"}</td>
                    <td>{r.client_name ?? "—"}</td>
                    <td>{r.document_type ?? "—"}</td>
                    <td>{r.account_manager ?? "—"}</td>

                    <td>{chip(r.draft_created)}</td>
                    <td>{chip(r.finance_reviewed)}</td>
                    <td>{chip(r.am_reviewed)}</td>
                    <td>{chip(r.signed_by_client)}</td>

                    <td>{r.draft_completed_date ?? "—"}</td>

                    <td>
                      {r.link_to_draft_sow ? (
                        <div className='link'>
                          <a
                            href={r.link_to_draft_sow}
                            target='_blank'
                            rel='noreferrer'
                          >
                            Draft SOW
                          </a>
                        </div>
                      ) : (
                        <div className='muted'>—</div>
                      )}
                      {r.link_to_existing_msa ? (
                        <div className='link'>
                          <a
                            href={r.link_to_existing_msa}
                            target='_blank'
                            rel='noreferrer'
                          >
                            MSA
                          </a>
                        </div>
                      ) : null}
                      {r.link_to_existing_nda ? (
                        <div className='link'>
                          <a
                            href={r.link_to_existing_nda}
                            target='_blank'
                            rel='noreferrer'
                          >
                            NDA
                          </a>
                        </div>
                      ) : null}
                    </td>

                    <td style={{ maxWidth: 360 }}>
                      <div className='muted'>
                        {r.comments ??
                          r.reason_no_draft_required ??
                          r.reason_no_finance_review_req ??
                          "—"}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Col>
      </Row>
    </Col>
  );
};

export default LegalDocRequestsReport;
